import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import ErrorBoundary from './ErrorBoundary';

// Debug function to log with timestamp
function debugLog(...args) {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}]`, ...args);
}

// Debug function to log errors with timestamp
function debugError(...args) {
  const timestamp = new Date().toISOString();
  console.error(`[${timestamp}]`, ...args);
}

// Global error handler
window.addEventListener('error', function(event) {
  debugError('Global error caught:', event.error || event.message, event);
  
  // Don't interfere with existing error handlers
  if (window.__reactErrorHandled) return;
  
  // Prevent infinite loops
  if (window.__reactErrorHandling) return;
  window.__reactErrorHandling = true;
  
  try {
    // Show error in the UI if possible
    const container = document.getElementById('root');
    if (container) {
      const error = event.error || { message: event.message || 'Unknown error' };
      container.innerHTML = `
        <div style="color: red; padding: 20px; max-width: 800px; margin: 0 auto;">
          <h1>Critical Application Error</h1>
          <p><strong>Message:</strong> ${error.message || 'No error message'}</p>
          <p><strong>Source:</strong> ${event.filename || 'unknown'}:${event.lineno || '?'}:${event.colno || '?'}</p>
          ${error.stack ? `
            <h3>Stack Trace:</h3>
            <pre style="white-space: pre-wrap; background: #f5f5f5; padding: 10px; border-radius: 4px; overflow-x: auto;">
              ${error.stack}
            </pre>
          ` : ''}
          <p>Please check the browser console for more details.</p>
        </div>
      `;
    }
  } catch (e) {
    debugError('Error in global error handler:', e);
  }
  
  return false;
});

// Unhandled promise rejections
window.addEventListener('unhandledrejection', function(event) {
  debugError('Unhandled promise rejection:', event.reason);
  
  // Show in UI if possible
  try {
    const container = document.getElementById('root');
    if (container) {
      const error = event.reason || { message: 'Unknown promise rejection' };
      container.innerHTML = `
        <div style="color: red; padding: 20px; max-width: 800px; margin: 0 auto;">
          <h1>Unhandled Promise Rejection</h1>
          <p><strong>Error:</strong> ${error.message || 'No error message'}</p>
          ${error.stack ? `
            <h3>Stack Trace:</h3>
            <pre style="white-space: pre-wrap; background: #f5f5f5; padding: 10px; border-radius: 4px; overflow-x: auto;">
              ${error.stack}
            </pre>
          ` : ''}
        </div>
      `;
    }
  } catch (e) {
    debugError('Error in unhandledrejection handler:', e);
  }
});

// Debug info
debugLog('1. Starting application initialization');
debugLog('React version:', React.version);
debugLog('User agent:', navigator.userAgent);
debugLog('Public URL:', process.env.PUBLIC_URL);
debugLog('NODE_ENV:', process.env.NODE_ENV);
debugLog('REACT_APP_GIT_COMMIT_HASH:', process.env.REACT_APP_GIT_COMMIT_HASH);

// Check if root element exists
const container = document.getElementById('root');
if (!container) {
  const errorMsg = 'FATAL: Could not find root element with id "root"';
  debugError(errorMsg);
  document.body.innerHTML = `
    <div style="color: red; padding: 20px; max-width: 800px; margin: 0 auto;">
      <h1>Root Element Not Found</h1>
      <p>${errorMsg}</p>
      <p>Please check if your public/index.html file contains an element with id="root"</p>
    </div>
  `;
  throw new Error(errorMsg);
}
debugLog('2. Found root container:', container);

// Create root
debugLog('3. Creating root...');
const root = createRoot(container);

debugLog('3.1. Root created:', root);

// Function to get the correct basename based on environment
function getBasename() {
  // In development, use empty basename
  if (process.env.NODE_ENV === 'development') {
    return '';
  }
  
  // In production, use the PUBLIC_URL or default to '/'
  // Ensure we don't get a single dot (.) as basename
  const base = process.env.PUBLIC_URL || '';
  return base === '.' ? '' : base;
}

// Function to render the app with error boundary
function renderApp() {
  debugLog('4. Starting render...');
  try {
    debugLog('4.1. Creating app element tree...');
    const basename = getBasename();
    debugLog('4.1.1. Using basename:', basename);
    
    const appElement = (
      <React.StrictMode>
        <ErrorBoundary>
          <BrowserRouter basename={basename}>
            <App />
          </BrowserRouter>
        </ErrorBoundary>
      </React.StrictMode>
    );
    
    debugLog('4.2. Rendering app...');
    root.render(appElement);
    debugLog('5. Render completed successfully');
  } catch (error) {
    debugError('Error during render:', error);
    
    // Show error in the UI
    try {
      if (container) {
        container.innerHTML = `
          <div style="color: red; padding: 20px; max-width: 800px; margin: 0 auto;">
            <h1>Application Error</h1>
            <p><strong>Message:</strong> ${error.message || 'No error message'}</p>
            <h3>Stack Trace:</h3>
            <pre style="white-space: pre-wrap; background: #f5f5f5; padding: 10px; border-radius: 4px; overflow-x: auto;">
              ${error.stack || 'No stack trace available'}
            </pre>
            <p>Check the browser console for more details.</p>
          </div>
        `;
      }
    } catch (innerError) {
      debugError('Error in error handler:', innerError);
    }
    
    // Re-throw to ensure it's logged
    throw error;
  }
}

// Initial render
try {
  renderApp();
} catch (error) {
  console.error('Fatal error during initial render:', error);
  // The error should already be displayed in the UI by renderApp()
}
